/**
 * 대기이벤트 ↔ 시스템 메트릭 **매핑** — 오라클 성능 분석 › 실시간 성능(2026-10-14, 사용자 요청).
 *
 * 실시간 성능 탭은 대기이벤트를 출발점으로 그 원인·상관 지표를 찾아 가는 화면이다. 위 그래프에서 대기 하나를 누르면
 * 「그 대기와 함께 볼 시스템 메트릭」 이 리본으로 뜨고, 고른 것이 아래 그래프에 그려진다. 그 짝을 여기서 정한다.
 *
 *  · `DEFAULT_MAP` 은 **기본값**이다(Oracle 의 일반적인 상관관계 — 어느 DB 에나 맞는 정답이 아니다). 사용자가 고친 것은
 *    서버에 사용자별로 저장되고(`perfMetricMapStore`) 여기 기본값보다 앞선다. 고치지 않은 키는 기본값이 좋아지면 따라온다.
 *  · 키는 세 가지: 대기이벤트 이름 · `class:<대기 그룹>` · `*`(고른 대기가 없을 때). 찾는 순서도 그 순서다 —
 *    이벤트에 맞는 것이 없으면 그 이벤트의 대기 그룹, 그것도 없으면 `*`.
 *  · 메트릭 이름은 V$SYSMETRIC 의 METRIC_NAME **그대로**다(데이터 키 — 번역하지 않는다). 버전·환경(PDB)에 따라 없는 지표가 있으므로
 *    화면은 카탈로그에 있는 것만 칩으로 세우고, 빠진 수를 함께 말한다.
 */

export const ANY_KEY = '*';
export const classKey = (waitClass) => `class:${waitClass}`;
/** 한 그래프에 그릴 수 있는 단위의 수 — 왼쪽 축 하나, 오른쪽 보조 축 하나 */
export const MAX_UNITS = 2;
/** 한 그래프에 그릴 메트릭 수의 상한(서버 `/sysmetric` 의 상한과 같다) */
export const MAX_SHOWN = 12;

const IO_READ = ['Physical Reads Per Sec', 'Physical Read IO Requests Per Sec', 'Physical Read Bytes Per Sec', 'Average Synchronous Single-Block Read Latency', 'I/O Megabytes per Second', 'I/O Requests per Second', 'Buffer Cache Hit Ratio'];
const PARSE = ['Hard Parse Count Per Sec', 'Total Parse Count Per Sec', 'Parse Failure Count Per Sec', 'Library Cache Hit Ratio', 'Library Cache Miss Ratio', 'Shared Pool Free %', 'Soft Parse Ratio'];
const REDO = ['Redo Generated Per Sec', 'Redo Writes Per Sec', 'DBWR Checkpoints Per Sec', 'Background Checkpoints Per Sec', 'Redo Allocation Hit Ratio'];
const ENQ = ['Enqueue Waits Per Sec', 'Enqueue Timeouts Per Sec', 'Enqueue Deadlocks Per Sec', 'Enqueue Requests Per Sec'];
const TEMP = ['Temp Space Used', 'Disk Sort Per Sec', 'Total PGA Used by SQL Workareas', 'PGA Cache Hit %', 'Physical Writes Direct Per Sec'];
const GC = ['Global Cache Average CR Get Time', 'Global Cache Average Current Get Time', 'GC CR Block Received Per Second', 'GC Current Block Received Per Second', 'Global Cache Blocks Lost'];

export const DEFAULT_MAP = {
  // 고른 대기가 없을 때 — 전체 부하를 읽는 기본 지표
  [ANY_KEY]: ['Average Active Sessions', 'Host CPU Utilization (%)', 'Database CPU Time Ratio', 'Database Wait Time Ratio', 'Executions Per Sec', 'User Transaction Per Sec'],

  // CPU — 대기가 아니라 일하는 중이다. 무엇이 CPU 를 쓰는가(읽기·파싱·실행 수)
  'ON CPU': ['Host CPU Utilization (%)', 'CPU Usage Per Sec', 'Database CPU Time Ratio', 'Logical Reads Per Sec', 'Executions Per Sec', 'Hard Parse Count Per Sec', 'Current OS Load'],

  // User I/O
  [classKey('User I/O')]: IO_READ,
  'db file sequential read': ['Average Synchronous Single-Block Read Latency', 'Physical Reads Per Sec', 'Physical Read IO Requests Per Sec', 'Total Index Scans Per Sec', 'Buffer Cache Hit Ratio', 'Logical Reads Per Sec'],
  'db file scattered read': ['Physical Reads Per Sec', 'Long Table Scans Per Sec', 'Total Table Scans Per Sec', 'Physical Read Bytes Per Sec', 'Buffer Cache Hit Ratio'],
  'db file parallel read': ['Physical Reads Per Sec', 'Physical Read IO Requests Per Sec', 'Physical Read Bytes Per Sec', 'Total Index Scans Per Sec'],
  'direct path read': ['Physical Reads Direct Per Sec', 'Long Table Scans Per Sec', 'Physical Read Bytes Per Sec', 'Active Parallel Sessions', 'Physical Reads Per Sec'],
  'direct path write': ['Physical Writes Direct Per Sec', 'Physical Write Bytes Per Sec', 'Physical Writes Per Sec'],
  'direct path read temp': TEMP,
  'direct path write temp': TEMP,
  'read by other session': ['Physical Reads Per Sec', 'Logical Reads Per Sec', 'Buffer Cache Hit Ratio', 'Long Table Scans Per Sec'],

  // System I/O — 백그라운드의 쓰기
  [classKey('System I/O')]: ['Physical Writes Per Sec', 'Physical Write IO Requests Per Sec', 'Physical Write Bytes Per Sec', 'Redo Writes Per Sec', 'DBWR Checkpoints Per Sec', 'Background Checkpoints Per Sec'],
  'log file parallel write': ['Redo Writes Per Sec', 'Redo Generated Per Sec', 'User Commits Per Sec', 'Physical Write Total Bytes Per Sec'],
  'db file parallel write': ['Physical Writes Per Sec', 'DB Block Changes Per Sec', 'DBWR Checkpoints Per Sec', 'Background Checkpoints Per Sec'],
  'control file parallel write': ['Redo Writes Per Sec', 'DBWR Checkpoints Per Sec', 'Background Checkpoints Per Sec'],

  // Commit
  [classKey('Commit')]: ['User Commits Per Sec', 'Redo Generated Per Sec', 'Redo Writes Per Sec', 'User Transaction Per Sec', 'Response Time Per Txn'],
  'log file sync': ['User Commits Per Sec', 'Redo Generated Per Sec', 'Redo Writes Per Sec', 'User Transaction Per Sec', 'Response Time Per Txn', 'Redo Generated Per Txn'],

  // Concurrency
  [classKey('Concurrency')]: ['Hard Parse Count Per Sec', 'Total Parse Count Per Sec', 'DB Block Changes Per Sec', 'Logical Reads Per Sec', 'Library Cache Hit Ratio', 'Session Count'],
  'buffer busy waits': ['DB Block Changes Per Sec', 'Logical Reads Per Sec', 'Leaf Node Splits Per Sec', 'Physical Reads Per Sec', 'User Transaction Per Sec'],
  'library cache lock': PARSE,
  'library cache pin': PARSE,
  'library cache: mutex X': PARSE,
  'cursor: pin S wait on X': PARSE,
  'cursor: pin S': PARSE,
  'cursor: mutex S': PARSE,
  'cursor: mutex X': PARSE,
  'latch: shared pool': PARSE,
  'row cache lock': ['Row Cache Hit Ratio', 'Row Cache Miss Ratio', 'Hard Parse Count Per Sec', 'Shared Pool Free %'],
  'latch: cache buffers chains': ['Logical Reads Per Sec', 'Consistent Read Gets Per Sec', 'DB Block Gets Per Sec', 'CPU Usage Per Sec'],
  'latch free': ['Logical Reads Per Sec', 'CPU Usage Per Sec', 'Hard Parse Count Per Sec', 'Executions Per Sec'],

  // Application — 락
  [classKey('Application')]: [...ENQ, 'User Transaction Per Sec', 'User Rollbacks Per Sec'],
  'enq: TX - row lock contention': ['Enqueue Waits Per Sec', 'Enqueue Timeouts Per Sec', 'Enqueue Deadlocks Per Sec', 'User Rollbacks Per Sec', 'DB Block Changes Per Sec', 'User Transaction Per Sec'],
  'enq: TM - contention': ['Enqueue Waits Per Sec', 'Enqueue Requests Per Sec', 'User Transaction Per Sec', 'DB Block Changes Per Sec'],

  // Configuration — 크기·설정이 모자라 생기는 대기
  [classKey('Configuration')]: ['Redo Generated Per Sec', 'DB Block Changes Per Sec', 'Redo Writes Per Sec', 'DBWR Checkpoints Per Sec', 'Background Checkpoints Per Sec'],
  'enq: HW - contention': ['DB Block Changes Per Sec', 'Physical Writes Direct Per Sec', 'Redo Generated Per Sec'],
  'log file switch completion': REDO,
  'log file switch (checkpoint incomplete)': REDO,
  'log file switch (archiving needed)': REDO,
  'log buffer space': REDO,
  'free buffer waits': ['Physical Writes Per Sec', 'DB Block Changes Per Sec', 'DBWR Checkpoints Per Sec', 'Physical Reads Per Sec'],
  'write complete waits': ['Physical Writes Per Sec', 'DB Block Changes Per Sec', 'DBWR Checkpoints Per Sec'],
  'enq: TX - allocate ITL entry': ['Enqueue Waits Per Sec', 'DB Block Changes Per Sec', 'User Transaction Per Sec'],

  // Network · Cluster · Scheduler · Other(병렬)
  [classKey('Network')]: ['Network Traffic Volume Per Sec', 'User Calls Per Sec', 'Executions Per Sec', 'SQL Service Response Time'],
  [classKey('Cluster')]: GC,
  [classKey('Scheduler')]: ['Host CPU Utilization (%)', 'CPU Usage Per Sec', 'Current OS Load', 'Average Active Sessions'],
  [classKey('Other')]: ['Active Parallel Sessions', 'PQ Slave Session Count', 'PQ QC Session Count', 'Queries parallelized Per Sec', 'Average Active Sessions'],
  [classKey('Administrative')]: ['Average Active Sessions', 'DB Block Changes Per Sec', 'Physical Writes Per Sec'],
  [classKey('Idle')]: ['Session Count', 'Current Logons Count', 'User Calls Per Sec'],
};

const listOf = (v) => (Array.isArray(v) ? v.filter(x => typeof x === 'string' && x) : null);

/**
 * 그 대기에 걸린 메트릭 — **순수 함수**.
 * @param event      대기이벤트 이름(없으면 `*`)
 * @param waitClass  그 이벤트의 대기 그룹(없을 수 있다)
 * @param userMap    사용자가 고친 것 { 키: [메트릭…] } — 빈 목록도 뜻이 있다(「붙이지 않는다」)
 * @returns {{ key, source:'user'|'default'|'none', level:'event'|'class'|'any', metrics:string[] }}
 */
export function resolveMetrics(event, waitClass, userMap) {
  const user = userMap || {};
  const tries = [];
  if (event) tries.push([event, 'event']);
  if (waitClass && waitClass !== 'CPU') tries.push([classKey(waitClass), 'class']);
  tries.push([ANY_KEY, 'any']);
  for (const [key, level] of tries) {
    const u = listOf(user[key]);
    if (u) return { key, source: 'user', level, metrics: u };
    const d = listOf(DEFAULT_MAP[key]);
    if (d) return { key, source: 'default', level, metrics: d };
  }
  return { key: ANY_KEY, source: 'none', level: 'any', metrics: [] };
}

/** 편집할 키의 지금 값 — 사용자 것이 있으면 그것, 없으면 기본값(그 키에 정확히 걸린 것만 — 위로 올라가 찾지 않는다) */
export function metricsForKey(key, userMap) {
  const u = listOf((userMap || {})[key]);
  if (u) return { source: 'user', metrics: u };
  const d = listOf(DEFAULT_MAP[key]);
  return d ? { source: 'default', metrics: d } : { source: 'none', metrics: [] };
}

/** 사용자 매핑에 키 하나를 적는다. 기본값과 같아지면 지운다(고친 것만 남긴다) — **순수 함수** */
export function withKey(userMap, key, metrics) {
  const next = { ...(userMap || {}) };
  const list = [...new Set((metrics || []).filter(Boolean))];
  const def = listOf(DEFAULT_MAP[key]);
  if (def && def.length === list.length && def.every((m, i) => m === list[i])) delete next[key];
  else next[key] = list;
  return next;
}
export function withoutKey(userMap, key) {
  const next = { ...(userMap || {}) };
  delete next[key];
  return next;
}

/** 편집 창의 왼쪽 목록 — 기본값의 키와 사용자가 더한 키. `*` 가 맨 앞, 그다음 대기 그룹, 그다음 이벤트(이름순) */
export function allKeys(userMap) {
  const keys = new Set([...Object.keys(DEFAULT_MAP), ...Object.keys(userMap || {})]);
  const rank = (k) => (k === ANY_KEY ? 0 : (k.startsWith('class:') ? 1 : (k === 'ON CPU' ? 2 : 3)));
  return [...keys].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * 고른 메트릭을 **단위별로 축에 나눈다** — 순수 함수.
 * 단위가 같으면 한 축에 여럿, 다른 단위가 끼면 오른쪽 보조 축으로. 세 번째 단위는 그릴 자리가 없다(축이 둘뿐이다).
 * 먼저 고른 것의 단위가 왼쪽이다.
 * @param metrics 고른 순서대로의 이름
 * @param unitOf  이름 → 단위(모르면 빈 글 — 모르는 단위끼리는 같은 단위로 본다)
 * @returns {{ left:string[], right:string[], leftUnit, rightUnit, dropped:string[] }}
 */
export function splitByUnit(metrics, unitOf) {
  const out = { left: [], right: [], leftUnit: null, rightUnit: null, dropped: [] };
  for (const m of metrics || []) {
    const u = String((unitOf && unitOf(m)) || '');
    if (out.leftUnit === null) { out.leftUnit = u; out.left.push(m); }
    else if (u === out.leftUnit) out.left.push(m);
    else if (out.rightUnit === null) { out.rightUnit = u; out.right.push(m); }
    else if (u === out.rightUnit) out.right.push(m);
    else out.dropped.push(m);
  }
  return out;
}

/** 지금 고른 것에 하나를 더 그릴 수 있는가 — 단위가 셋째면(또는 수가 넘으면) 못 그린다 */
export function canShow(selected, name, unitOf) {
  if ((selected || []).includes(name)) return true;
  if ((selected || []).length >= MAX_SHOWN) return false;
  return splitByUnit([...(selected || []), name], unitOf).dropped.length === 0;
}
